import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Folder, Trash2, Download, FileText, Image as ImageIcon } from "lucide-react";

function iconFor(name = "") {
  const lower = name.toLowerCase();
  if (lower.match(/\.(png|jpe?g|gif|webp)$/)) return ImageIcon;
  return FileText;
}

function formatBytes(b = 0) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ entity_type: "", entity_id: "", description: "" });
  const fileRef = useRef(null);

  const load = async () => { const r = await api.get("/documents?limit=200"); setDocs(r.data); };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", file);
    if (meta.entity_type) fd.append("entity_type", meta.entity_type);
    if (meta.entity_id) fd.append("entity_id", meta.entity_id);
    if (meta.description) fd.append("description", meta.description);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setOpen(false); setMeta({ entity_type: "", entity_id: "", description: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = (id) => {
    window.open(`${API_BASE}/documents/${id}/download`, "_blank");
  };

  return (
    <div data-testid="documents-page">
      <PageHeader title="Documents & Media" subtitle="Document Engine"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="upload-document-button"><Upload className="w-3.5 h-3.5 mr-1.5"/> Upload</Button></DialogTrigger>
            <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
              <DialogHeader><DialogTitle className="font-heading">Upload document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">File</Label><Input type="file" ref={fileRef} className="bg-hocp-bgCard border-hocp-border mt-1.5 file:text-hocp-textPrimary file:bg-transparent file:border-0 file:text-xs" data-testid="document-file-input"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Entity type</Label><Input value={meta.entity_type} onChange={(e) => setMeta({ ...meta, entity_type: e.target.value })} placeholder="hotel" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="document-entity-type-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Entity ID</Label><Input value={meta.entity_id} onChange={(e) => setMeta({ ...meta, entity_id: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="document-entity-id-input"/></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Description</Label><Input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="document-description-input"/></div>
              </div>
              <DialogFooter><Button onClick={upload} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-document-button">Upload</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {docs.length === 0 ? <EmptyState icon={Folder} title="No documents yet" subtitle="Upload any file: PDF, image, office document. Versioning and entity linking supported."/> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => {
            const Icon = iconFor(d.filename);
            return (
              <Card key={d.id} className="p-4" data-testid={`document-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded bg-hocp-bgSubtle border border-hocp-border flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-hocp-textMuted" strokeWidth={1.5}/></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-white font-medium truncate">{d.filename}</div>
                    <div className="mono text-[10px] text-hocp-textMuted mt-0.5">{formatBytes(d.size)} · v{d.version}</div>
                    {d.entity_type && <div className="mono text-[10px] text-hocp-textMuted mt-0.5">→ {d.entity_type} {d.entity_id?.slice(-8)}</div>}
                  </div>
                </div>
                {d.description && <div className="text-[11px] text-hocp-textMuted mt-3 line-clamp-2">{d.description}</div>}
                <div className="mt-3 pt-3 border-t border-hocp-border flex items-center justify-between gap-2">
                  <span className="mono text-[10px] text-hocp-textTertiary">{new Date(d.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => download(d.id)} className="h-7 px-2 text-[11px] text-hocp-textMuted hover:text-hocp-deepTeal" data-testid={`download-document-${d.id}`}><Download className="w-3.5 h-3.5"/></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-document-${d.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      }
    </div>
  );
}
